/* ============================================================
   /app/leads — "My leads": the signed-in employee's own captured leads
   (scans, and anything else attributed to them). Read-only by design —
   editing/deleting a lead stays an admin action on /admin/leads, which
   is the master view across everyone. This is just "what did I do."
   ============================================================ */
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { HttpError } from "../../lib/api";
import { authedGetBlobUrl, authedGetJson, isLoggedIn } from "../../lib/auth";

type Lead = {
  id: string;
  name: string | null;
  title: string | null;
  company: string | null;
  email: string | null;
  mobile: string | null;
  landline: string | null;
  city: string | null;
  source_tag: string | null;
  status: string;
  has_card_image: boolean;
  created_at: string;
};

/** Scanned-card thumbnail for a "My leads" row. Fetches the image as an
 * authenticated blob (the endpoint needs the app JWT, which a plain <img
 * src> can't carry) and revokes the object URL on unmount. */
function LeadThumb({ leadId }: { leadId: string }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    authedGetBlobUrl(`/api/me/leads/${leadId}/card-image`)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => {
        // best-effort — a missing/failed thumbnail just doesn't render
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [leadId]);

  if (!url) {
    return (
      <div className="w-11 h-11 rounded-lg bg-ink/5 border border-ink/10 shrink-0 animate-pulse" />
    );
  }
  return (
    <img
      src={url}
      alt="Scanned business card"
      className="w-11 h-11 rounded-lg object-cover border border-ink/10 shrink-0"
    />
  );
}

type LeadsPage = {
  items: Lead[];
  total: number;
  limit: number;
  offset: number;
};

const PAGE_SIZE = 50;

type State =
  | { kind: "unauthenticated" }
  | { kind: "loading" }
  | { kind: "ready"; page: LeadsPage }
  | { kind: "error"; message: string };

export default function AppLeads() {
  const [state, setState] = useState<State>(
    isLoggedIn() ? { kind: "loading" } : { kind: "unauthenticated" },
  );
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    if (!isLoggedIn()) return;
    setState((s) => (s.kind === "ready" ? s : { kind: "loading" }));
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (q) params.set("q", q);
    let cancelled = false;
    authedGetJson<LeadsPage>(`/api/me/leads?${params.toString()}`)
      .then((page) => {
        if (!cancelled) setState({ kind: "ready", page });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof HttpError && e.status === 401) {
          setState({ kind: "unauthenticated" });
        } else {
          const detail = e instanceof HttpError ? ` (HTTP ${e.status})` : "";
          setState({ kind: "error", message: `Couldn't load your leads${detail}.` });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [q, offset, retryTick]);

  if (state.kind === "unauthenticated") {
    return <Navigate to="/app/login" replace />;
  }

  return (
    <main className="min-h-screen px-6 pb-6 pt-safe bg-paper">
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-xl font-bold text-ink">My leads</h1>
          <Link to="/app" className="text-xs text-ink/60 underline">
            ← My card
          </Link>
        </div>

        <input
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOffset(0);
          }}
          placeholder="Search name, company, email…"
          className="w-full rounded-lg border border-ink/15 bg-white px-4 py-3 text-sm text-ink
                     placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-signal"
        />

        {state.kind === "loading" && (
          <p className="text-ink/60 text-sm text-center py-8">Loading…</p>
        )}
        {state.kind === "error" && (
          <div className="text-center py-8 space-y-3">
            <p className="text-red-600 text-sm">{state.message}</p>
            <button
              type="button"
              onClick={() => setRetryTick((t) => t + 1)}
              className="rounded-lg border border-ink/15 px-4 py-2 text-sm text-ink"
            >
              Try again
            </button>
          </div>
        )}
        {state.kind === "ready" && state.page.items.length === 0 && (
          <p className="text-ink/60 text-sm text-center py-8">
            {q ? "No leads match your search." : "No leads captured yet — scan a card to get started."}
          </p>
        )}
        {state.kind === "ready" && state.page.items.length > 0 && (
          <>
            <p className="text-ink/40 text-xs">{state.page.total} total</p>
            <ul className="space-y-2">
              {state.page.items.map((lead) => (
                <li
                  key={lead.id}
                  className="rounded-lg border border-ink/10 bg-white px-4 py-3 space-y-1"
                >
                  <div className="flex items-start gap-3">
                    {lead.has_card_image && <LeadThumb leadId={lead.id} />}
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-semibold text-sm text-ink">
                          {lead.name || <span className="text-ink/40 font-normal">Unnamed</span>}
                        </span>
                        <span className="text-[10px] uppercase tracking-wide text-ink/40 shrink-0">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {(lead.title || lead.company) && (
                        <div className="text-xs text-ink/60">
                          {[lead.title, lead.company].filter(Boolean).join(" · ")}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink/50">
                        {lead.email && <span>{lead.email}</span>}
                        {lead.mobile && <span dir="ltr">{lead.mobile}</span>}
                        {lead.city && <span>{lead.city}</span>}
                      </div>
                      <div className="flex items-center gap-2 pt-1">
                        <span className="text-[10px] uppercase tracking-wide rounded-full border border-ink/15 px-2 py-0.5 text-ink/50">
                          {lead.source_tag ?? "—"}
                        </span>
                        <span className="text-[10px] uppercase tracking-wide rounded-full border border-signal/40 bg-signal/10 px-2 py-0.5 text-ink/70">
                          {lead.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {state.page.total > PAGE_SIZE && (
              <div className="flex items-center justify-between text-xs text-ink/60 pt-2">
                <span>
                  {offset + 1}–{Math.min(offset + PAGE_SIZE, state.page.total)} of{" "}
                  {state.page.total}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    className="rounded-lg border border-ink/15 px-3 py-1.5 disabled:opacity-40"
                  >
                    ← Prev
                  </button>
                  <button
                    type="button"
                    disabled={offset + PAGE_SIZE >= state.page.total}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                    className="rounded-lg border border-ink/15 px-3 py-1.5 disabled:opacity-40"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
